import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, Tag, ArrowRight, BookOpen, FileText, Settings, Briefcase, Scale, Code2 } from 'lucide-react';
import { BlogPost, BlogCategory } from '../../types/blog';
import { formatDate, getCategoryColor } from '../../utils/blog';

interface BlogCardProps {
  post: BlogPost;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  // Get icon and gradient based on category
  const getCategoryStyle = (category: BlogCategory) => {
    const styles: Record<BlogCategory, { icon: React.ReactNode; gradient: string }> = {
      [BlogCategory.FUNDAMENTALS]: {
        icon: <BookOpen className="w-16 h-16" />,
        gradient: 'from-blue-400 to-blue-600'
      },
      [BlogCategory.PRACTICAL_GUIDES]: {
        icon: <FileText className="w-16 h-16" />,
        gradient: 'from-green-400 to-green-600'
      },
      [BlogCategory.TOOLS]: {
        icon: <Settings className="w-16 h-16" />,
        gradient: 'from-purple-400 to-purple-600'
      },
      [BlogCategory.BUSINESS]: {
        icon: <Briefcase className="w-16 h-16" />,
        gradient: 'from-orange-400 to-orange-600'
      },
      [BlogCategory.LEGAL]: {
        icon: <Scale className="w-16 h-16" />,
        gradient: 'from-red-400 to-red-600'
      },
      [BlogCategory.FRAMEWORKS]: {
        icon: <Code2 className="w-16 h-16" />,
        gradient: 'from-indigo-400 to-indigo-600'
      }
    };
    
    return styles[category] || { icon: <BookOpen className="w-16 h-16" />, gradient: 'from-gray-400 to-gray-600' };
  };

  const categoryStyle = getCategoryStyle(post.category);

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
      {/* Featured Image or Placeholder */}
      <div className="relative h-48 overflow-hidden">
        {post.image ? (
          <>
            <img
              src={post.image}
              alt={`Cover image for ${post.title}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            {post.featured && (
              <span className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Featured
              </span>
            )}
          </>
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${categoryStyle.gradient} flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
            <div className="text-white opacity-50 group-hover:opacity-70 transition-opacity">
              {categoryStyle.icon}
            </div>
            {post.featured && (
              <span className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Featured
              </span>
            )}
          </div>
        )}
      </div>
      
      <div className="p-6">
        {/* Category Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
            {post.category}
          </span>
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          <Link to={`/blog/${post.slug}`} className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
            {post.title}
          </Link>
        </h2>
        
        {/* Description */}
        <p className="text-gray-600 mb-4 line-clamp-3">
          {post.description}
        </p>
        
        {/* Meta Information */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" aria-hidden="true" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" aria-hidden="true" />
            <span>{post.readingTime.text}</span>
          </div>
        </div>
        
        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
              >
                <Tag className="w-3 h-3" aria-hidden="true" />
                {tag}
              </span>
            ))}
            {post.tags.length > 3 && (
              <span className="text-xs text-gray-500">+{post.tags.length - 3} more</span>
            )}
          </div>
        )}
        
        {/* Read More Link */}
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium group/link focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 -ml-2"
          aria-label={`Read article: ${post.title}`}
        >
          Read More
          <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
};
